import { useState, type ReactNode } from "react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { useTransferParticipant } from "../api/hooks";
import type { Group, Participant } from "../api/types";

/**
 * Confirms moving one participant's whole history onto another, then deleting
 * the emptied one (issue #448).
 *
 * The copy is explicit about what happens because the operation is not
 * reversible from the UI: expenses and payments are rewritten in place, and a
 * payment between the two is dropped (once they're one person, a payment to
 * yourself means nothing).
 */
export function TransferParticipantDialog({
  group,
  source,
  targets,
  open,
  onOpenChange,
  onTransferred,
}: {
  group: Group;
  source: Participant;
  targets: Participant[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTransferred: () => void;
}): ReactNode {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {/* Mounted only while open so the picker and any error reset between
            openings, rather than carrying a stale selection. */}
        {open ? (
          <TransferBody
            group={group}
            source={source}
            targets={targets}
            onOpenChange={onOpenChange}
            onTransferred={onTransferred}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function TransferBody({
  group,
  source,
  targets,
  onOpenChange,
  onTransferred,
}: {
  group: Group;
  source: Participant;
  targets: Participant[];
  onOpenChange: (open: boolean) => void;
  onTransferred: () => void;
}): ReactNode {
  const transfer = useTransferParticipant(group.id);
  const [targetId, setTargetId] = useState<string>(targets[0]?.id ?? "");

  async function onConfirm(): Promise<void> {
    if (!targetId) return;
    try {
      await transfer.mutateAsync({
        participantId: source.id,
        body: { targetParticipantId: targetId },
      });
      onTransferred();
    } catch {
      // Rendered below; keep the dialog open so the OWNER can retry or pick a
      // different target. A partial transfer is safe to re-run.
    }
  }

  const targetName =
    targets.find((p) => p.id === targetId)?.displayName ?? "them";

  return (
    <>
      <DialogHeader>
        <DialogTitle>Move {source.displayName}&apos;s transactions</DialogTitle>
        <DialogDescription>
          Every expense and payment involving {source.displayName} will be
          reassigned to {targetName}, and {source.displayName} will be removed
          from {group.label}. Balances don&apos;t change overall — they just
          move onto {targetName}.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3">
        <Label htmlFor="transfer-target">Move everything to</Label>
        <select
          id="transfer-target"
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          className="flex h-11 w-full rounded-sm bg-surface-container-low px-4 text-body-md text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {targets.map((p) => (
            <option key={p.id} value={p.id}>
              {p.displayName}
              {p.type === "GUEST" ? " (guest)" : ""}
            </option>
          ))}
        </select>
        <p className="text-label-sm text-on-surface-variant">
          A payment between the two is deleted rather than moved: once
          they&apos;re one person, a payment to yourself means nothing.
        </p>
      </div>

      {transfer.error ? (
        <p className="text-label-md text-error">
          Couldn&apos;t move everything. Nothing is lost — the transfer picks up
          where it left off if you try again.
        </p>
      ) : null}

      <DialogFooter>
        <Button
          type="button"
          variant="ghost"
          onClick={() => onOpenChange(false)}
          disabled={transfer.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={onConfirm}
          disabled={!targetId || transfer.isPending}
        >
          {transfer.isPending ? "Moving…" : "Move and remove"}
        </Button>
      </DialogFooter>
    </>
  );
}
